/* oxlint-disable max-lines */
import {
  ROUTES_CUSTOMERS,
  ROUTES_EMPLOYEE_OVERVIEWS,
  ROUTES_EMPLOYEES,
  ROUTES_PARTNERS,
  ROUTES_SUPERVISORS,
} from '@/app/data/route_constants';
import { FT } from '@/app/data/flipper_constants';

// A navbar item is active on every route whose name starts with its `prefix`. Detail pages that sit
// outside that route tree list their route name in `prefixes_active` so the section stays highlighted.
export const isNavbarItemActive = (item, route_name) => {
  if (!route_name) return false;
  const prefixes = [item.prefix, ...(item.prefixes_active || [])];
  return prefixes.some((prefix) => Boolean(prefix) && route_name.startsWith(prefix));
};

// Customer
export const NAVBAR_ITEMS_CUSTOMER = [
  {
    data_test: 'customer-navbar-dashboard',
    icon: 'regular-grid-horizontal',
    name: ROUTES_CUSTOMERS.DASHBOARD,
    prefix: ROUTES_CUSTOMERS.DASHBOARD,
    title: 'navbar.customer.dashboard',
  },
  {
    data_test: 'customer-navbar-announcements',
    icon: 'regular-list-ul',
    name: ROUTES_CUSTOMERS.ANNOUNCEMENTS,
    prefix: ROUTES_CUSTOMERS.ANNOUNCEMENTS,
    title: 'navbar.customer.announcements',
    flipper: FT.ANNOUNCEMENTS,
  },
  {
    data_test: 'customer-navbar-tickets',
    icon: 'regular-comment',
    name: ROUTES_CUSTOMERS.TICKETS,
    prefix: ROUTES_CUSTOMERS.TICKETS,
    title: 'navbar.customer.tickets',
    flipper: FT.TICKETS,
  },
  {
    data_test: 'customer-navbar-documents',
    icon: 'regular-file-lines',
    name: ROUTES_CUSTOMERS.DOCUMENTS_CATEGORIES,
    prefix: ROUTES_CUSTOMERS.DOCUMENTS,
    title: 'navbar.customer.documents',
    flipper: FT.DOCUMENTS,
  },
  {
    data_test: 'customer-navbar-decisions',
    icon: 'regular-signs-post',
    name: ROUTES_CUSTOMERS.DECISIONS,
    prefix: 'customersDecision',
    title: 'navbar.customer.decisions',
    flipper: FT.DECISIONS_CORE,
  },
  {
    data_test: 'customer-navbar-installments',
    icon: 'regular-receipt',
    name: ROUTES_CUSTOMERS.INSTALLMENT_TERMS,
    prefix: 'customersInstallmentTerms',
    title: 'navbar.customer.installments',
    flipper: FT.FINANCIALS,
  },
  {
    data_test: 'customer-navbar-aftercare',
    icon: 'regular-house-chimney-heart',
    name: ROUTES_CUSTOMERS.ISSUES,
    prefix: 'customersIssues',
    title: 'navbar.customer.after_care',
    flipper: FT.AFTERCARE,
  },
  {
    data_test: 'customer-navbar-inspirationlists',
    icon: 'regular-lightbulb',
    name: ROUTES_CUSTOMERS.INSPIRATION_LISTS,
    prefix: 'customersInspirationLists',
    title: 'navbar.customer.inspirations',
    flipper: FT.INSPIRATIONS,
  },
  {
    data_test: 'customer-navbar-questionlists',
    icon: 'regular-circle-question',
    name: ROUTES_CUSTOMERS.QUESTION_LISTS,
    prefix: ROUTES_CUSTOMERS.QUESTION_LISTS,
    title: 'navbar.customer.question_lists',
    flipper: FT.FAQS,
  },
  {
    data_test: 'customer-navbar-projectinformation',
    icon: 'regular-circle-info',
    name: ROUTES_CUSTOMERS.PROJECT_INFORMATION,
    prefix: ROUTES_CUSTOMERS.PROJECT_INFORMATION,
    title: 'navbar.customer.projectinfo',
  },
];

// Employee

export const NAVBAR_ITEMS_EMPLOYEES_SECONDARY_NAVBAR = [
  {
    data_test: 'project-navbar-information',
    icon: 'solid-info',
    name: ROUTES_EMPLOYEES.PROJECT_INFORMATION_LEVELS_INFORMATION,
    prefix: 'employeesProjectInformation',
    title: 'navbar.employee.project.info',
    hidden_skip_customer: false,
  },
  {
    data_test: 'project-navbar-milestones',
    icon: 'solid-timeline',
    name: ROUTES_EMPLOYEES.PROJECT_MILESTONES,
    prefix: ROUTES_EMPLOYEES.PROJECT_MILESTONES,
    title: 'navbar.employee.project.milestones',
    flipper: FT.WORKFLOWS_PHASES,
  },
  {
    data_test: 'project-navbar-sales',
    icon: 'solid-handshake',
    name: ROUTES_EMPLOYEES.PROJECT_SALES,
    prefix: 'employeesProjectSales',
    title: 'navbar.employee.project.sales',
    flipper: FT.SALES,
    hidden_skip_customer: true,
  },
  {
    data_test: 'project-navbar-todolists',
    icon: 'regular-square-check',
    name: ROUTES_EMPLOYEES.PROJECT_TODO_LISTS,
    prefix: ROUTES_EMPLOYEES.PROJECT_TODO_LISTS,
    title: 'navbar.employee.project.todos',
    flipper: FT.TODOS,
    hidden_skip_customer: false,
  },
  {
    data_test: 'project-navbar-tasks',
    name: ROUTES_EMPLOYEES.PROJECT_TASKS,
    prefix: ROUTES_EMPLOYEES.PROJECT_TASKS,
    title: 'navbar.employee.project.tasks',
    flipper: FT.TASKS,
    hidden_skip_customer: false,
  },
  {
    data_test: 'project-navbar-documents',
    icon: 'regular-file-lines',
    name: ROUTES_EMPLOYEES.PROJECT_DOCUMENTS,
    prefix: ROUTES_EMPLOYEES.PROJECT_DOCUMENTS,
    title: 'navbar.employee.project.documents',
    flipper: FT.DOCUMENTS,
    hidden_skip_customer: false,
  },
  {
    data_test: 'project-navbar-tickets',
    icon: 'regular-comment',
    name: ROUTES_EMPLOYEES.PROJECT_TICKETS,
    prefix: ROUTES_EMPLOYEES.PROJECT_TICKETS,
    title: 'navbar.employee.tickets',
    flipper: FT.TICKETS,
    hidden_skip_customer: true,
  },
  {
    data_test: 'project-navbar-decisions',
    flipper: FT.DECISIONS_CORE,
    icon: 'regular-signs-post',
    name: ROUTES_EMPLOYEES.PROJECT_DECISION_TYPES,
    prefix: 'employeesProjectDecision',
    title: 'navbar.employee.project.decisions',
    hidden_skip_customer: false,
  },
  {
    data_test: 'project-navbar-installments',
    icon: 'regular-receipt',
    name: ROUTES_EMPLOYEES.PROJECT_INSTALLMENT_GROUPS_LIST,
    prefix: 'employeesProjectInstallment',
    title: 'navbar.employee.project.installments',
    flipper: FT.FINANCIALS,
    hidden_skip_customer: true,
  },
  {
    data_test: 'project-navbar-aftercare',
    icon: 'regular-house-chimney-heart',
    name: ROUTES_EMPLOYEES.PROJECT_ISSUE_LIST_TYPES,
    prefix: 'employeesProjectIssueList',
    title: 'navbar.employee.project.after_care',
    flipper: FT.AFTERCARE,
    hidden_skip_customer: true,
  },
  {
    data_test: 'project-navbar-announcements',
    icon: 'regular-newspaper',
    name: ROUTES_EMPLOYEES.PROJECT_ANNOUNCEMENTS,
    prefix: ROUTES_EMPLOYEES.PROJECT_ANNOUNCEMENTS,
    title: 'navbar.employee.project.news',
    flipper: FT.ANNOUNCEMENTS,
    hidden_skip_customer: false,
  },
  {
    data_test: 'project-navbar-surveys',
    icon: 'regular-clipboard-list',
    name: ROUTES_EMPLOYEES.PROJECT_SURVEY_TYPES,
    prefix: 'employeesProjectSurvey',
    title: 'navbar.employee.project.surveys',
    flipper: FT.SURVEYS,
    hidden_skip_customer: false,
  },
  {
    data_test: 'project-navbar-questionlists',
    icon: 'regular-circle-question',
    name: ROUTES_EMPLOYEES.PROJECT_QUESTION_LISTS,
    prefix: ROUTES_EMPLOYEES.PROJECT_QUESTION_LISTS,
    title: 'navbar.employee.project.question_lists',
    flipper: FT.FAQS,
    hidden_skip_customer: false,
  },
];

export const NAVBAR_ITEMS_EMPLOYEES_OVERVIEW_NAVBAR = [
  {
    data_test: 'project-navbar-all-projects-overview',
    icon: 'regular-building',
    name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_ALL_PROJECTS,
    prefix: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_ALL_PROJECTS,
    title: 'navbar.employee.all_projects',
  },
  {
    data_test: 'project-navbar-milestones-overview',
    icon: 'regular-timeline',
    name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_MILESTONES,
    prefix: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_MILESTONES,
    title: 'navbar.employee.project.milestones',
    flipper: FT.WORKFLOWS_PHASES,
  },
  {
    data_test: 'project-navbar-tasks-overview',
    flipper: FT.TODOS,
    icon: 'regular-square-check',
    name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_TASKS,
    prefix: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_TASKS,
    title: 'navbar.employee.project.todos',
  },
  {
    data_test: 'project-navbar-task-assignments-overview',
    flipper: FT.TASKS,
    name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_TASK_ASSIGNMENTS,
    prefix: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_TASK_ASSIGNMENTS,
    title: 'navbar.employee.project.tasks',
  },
  {
    data_test: 'project-navbar-documents-overview',
    icon: 'regular-file-lines',
    name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_DOCUMENTS,
    prefix: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_DOCUMENTS,
    title: 'navbar.employee.project.documents',
    flipper: FT.DOCUMENTS,
  },
  {
    data_test: 'project-navbar-conversations-overview',
    icon: 'regular-comment',
    name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_CONVERSATIONS,
    prefix: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_CONVERSATIONS,
    prefixes_active: [ROUTES_EMPLOYEES.CLIENT_TICKETS_SHOW],
    title: 'navbar.employee.tickets',
    flipper: FT.TICKETS,
  },
  {
    data_test: 'project-navbar-decisions-overview',
    flipper: FT.DECISIONS_CORE,
    icon: 'regular-signs-post',
    name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_DECISIONS,
    prefix: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_DECISIONS,
    title: 'navbar.employee.project.decisions',
  },
  {
    data_test: 'project-navbar-financials-overview',
    flipper: FT.FINANCIALS,
    icon: 'regular-receipt',
    name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_FINANCIALS,
    prefix: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_FINANCIALS,
    title: 'navbar.employee.project.installments',
  },
  {
    data_test: 'project-navbar-aftercare-overview',
    flipper: FT.AFTERCARE,
    icon: 'regular-house-chimney-heart',
    name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_AFTERCARE,
    prefix: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_AFTERCARE,
    title: 'navbar.employee.project.after_care',
  },
  {
    data_test: 'project-navbar-news-overview',
    flipper: FT.ANNOUNCEMENTS,
    icon: 'regular-newspaper',
    name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_NEWS,
    prefix: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_NEWS,
    title: 'navbar.employee.project.news',
  },
  {
    data_test: 'project-navbar-surveys-overview',
    flipper: FT.SURVEYS,
    icon: 'regular-clipboard-list',
    name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_SURVEYS,
    prefix: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_SURVEYS,
    title: 'navbar.employee.project.surveys',
  },
  {
    data_test: 'project-navbar-faqs-overview',
    flipper: FT.FAQS,
    icon: 'regular-circle-question',
    name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_FAQS,
    prefix: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_FAQS,
    title: 'navbar.employee.project.question_lists',
  },
];

// Partner

export const NAVBAR_ITEMS_PARTNERS_CLIENT = [
  {
    name: ROUTES_PARTNERS.CLIENT_PROJECTS,
    prefix: ROUTES_PARTNERS.CLIENT_PROJECTS,
    icon: 'solid-house',
    title: 'navbar.partner.projects',
    data_test: 'partner-navbar-projects',
  },
];

export const NAVBAR_ITEMS_PARTNERS_PROJECT_ACTIVE_ROLE_NEEDED = [
  ROUTES_PARTNERS.PROJECT_DECISION_TYPES,
  ROUTES_PARTNERS.PROJECT_ISSUE_LIST_TYPES,
];

export const NAVBAR_ITEMS_PARTNERS_PROJECT = [
  {
    data_test: 'project-navbar-information',
    icon: 'solid-info',
    name: ROUTES_PARTNERS.PROJECT_INFORMATION,
    prefix: ROUTES_PARTNERS.PROJECT_INFORMATION,
    title: 'navbar.employee.project.info',
  },
  {
    name: ROUTES_PARTNERS.PROJECT_DECISION_TYPES,
    flipper: FT.DECISIONS_PARTNERS,
    prefix: 'partnersProjectDecision',
    icon: 'regular-signs-post',
    title: 'navbar.partner.project.decisions',
    data_test: 'project-navbar-decisions',
  },
  {
    data_test: 'project-navbar-tickets',
    icon: 'regular-comment',
    flipper: FT.TICKETS,
    name: ROUTES_PARTNERS.PROJECT_TICKETS,
    prefix: ROUTES_PARTNERS.PROJECT_TICKETS,
    title: 'navbar.partner.project.tickets',
  },
  {
    data_test: 'project-navbar-documents',
    icon: 'regular-file-lines',
    name: ROUTES_PARTNERS.PROJECT_DOCUMENTS,
    prefix: ROUTES_PARTNERS.PROJECT_DOCUMENTS,
    title: 'navbar.partner.project.documents',
  },
  {
    data_test: 'project-navbar-aftercare',
    icon: 'regular-house-chimney-heart',
    name: ROUTES_PARTNERS.PROJECT_ISSUE_LIST_TYPES,
    prefix: 'partnersProjectIssueList',
    title: 'navbar.partner.project.after_care',
    flipper: FT.AFTERCARE,
  },
];

export const NAVBAR_TABLET_ITEMS_PARTNERS_PROJECT = [
  {
    data_test: 'project-navbar-allprojects',
    icon: 'solid-arrow-left',
    name: ROUTES_PARTNERS.CLIENT_PROJECTS,
    title: 'project.labels.projects',
  },
  ...NAVBAR_ITEMS_PARTNERS_PROJECT,
];

// Supervisor

export const NAVBAR_ITEMS_SUPERVISORS = [
  {
    icon: 'solid-users',
    prefix: ROUTES_SUPERVISORS.CLIENT,
    name: ROUTES_SUPERVISORS.CLIENTS,
    title: 'Clients',
  },
  {
    icon: 'solid-user',
    prefix: ROUTES_SUPERVISORS.USER,
    name: ROUTES_SUPERVISORS.USERS_SEARCH,
    title: 'Users',
  },
  {
    icon: 'solid-code-branch',
    prefix: ROUTES_SUPERVISORS.AUDIT_LOG_DATA,
    name: ROUTES_SUPERVISORS.AUDIT_LOG_DATAS_SEARCH,
    title: 'Audit',
  },
  {
    icon: 'solid-robot',
    prefix: ROUTES_SUPERVISORS.AI_ANALYTICS,
    name: ROUTES_SUPERVISORS.AI_ANALYTICS,
    title: 'AI usage',
  },
];
